import { FALLBACK_FONT_CACHE_NAME, FALLBACK_FONT_CACHE_PREFIX, FALLBACK_SCRIPT_FONT_SPECS } from './constants';
import type { FontFallback } from './types';

const SCRIPT_MATCHERS = new Map(
    FALLBACK_SCRIPT_FONT_SPECS.flatMap(({ scripts }) =>
        scripts.map((script: string) => [script, new RegExp(`\\p{Script=${script}}`, 'u')] as const),
    ),
);

/**
 * 检测文本中出现了哪些「已配置」的 script。
 *
 * 只检测传入的已配置 tag；空文本、未配置 script 与非法 tag 都不会命中。
 *
 * @param text - 待检测文本。
 * @returns 文本中实际出现的已配置 script tag 集合。
 */
export const detectConfiguredScripts = (text: string): Set<string> => {
    const matched = new Set<string>();
    if (!text) return matched;

    for (const [script, matcher] of SCRIPT_MATCHERS) {
        if (matcher.test(text)) matched.add(script);
    }
    return matched;
};

/**
 * 拉取 fallback 全量字体字节；Cache Storage 失败时降级为网络，不影响正确性
 *
 * @param fallback - 业务配置的 fallback 字体配置
 * @param src - 字体源 URL
 * @returns - 包含字体字节和来源的对象
 */
export const fetchFallbackFontBytes = async (fallback: FontFallback, src: string): Promise<Uint8Array> => {
    const cacheApi = globalThis.caches;

    let cache: Cache | undefined;
    let cacheKey: string | undefined;

    if (cacheApi) {
        try {
            cache = await cacheApi.open(FALLBACK_FONT_CACHE_NAME);

            const params = new URLSearchParams({ id: fallback.id, src });
            cacheKey = `${FALLBACK_FONT_CACHE_PREFIX}${encodeURIComponent(fallback.id)}?${params.toString()}`;

            const cached = await cache.match(cacheKey);
            if (cached) return new Uint8Array(await cached.arrayBuffer());
        } catch {
            cache = undefined;
            cacheKey = undefined;
        }
    }

    const response = await fetch(src, { signal: AbortSignal.timeout(30_000) });
    if (!response.ok) throw new Error(`Failed to fetch fallback font: ${response.status}`);

    const bytes = new Uint8Array(await response.arrayBuffer());
    if (cache && cacheKey) {
        try {
            await cache.put(
                cacheKey,
                new Response(bytes.slice(), {
                    headers: {
                        'Content-Type': 'font/ttf',
                        'X-XDesign-Fallback-Id': fallback.id,
                    },
                }),
            );
        } catch {
            // Cache Storage 是性能优化，写入失败不应阻断 fallback 注册。
        }
    }

    return bytes;
};
